/**
 * Único ficheiro com texto visível para o utilizador (RNF-024). Nenhum
 * componente escreve uma frase directamente, mesmo havendo um só idioma:
 * a camada existe desde o primeiro dia porque acrescentá-la depois obriga a
 * percorrer todos os ecrãs.
 *
 * Microtexto conforme a skill design-mimo: directo, calmo, sem entusiasmo
 * forçado. Sem exclamações no erro. Sem gerúndio brasileiro.
 */
export const ptPT = {
  // O nome do produto. Resolve o Q1 dos requisitos, que estava em aberto.
  // Fica separado do título porque o nome é a marca e não se traduz; o resto
  // desta camada existe para poder ser traduzido.
  'app.nome': 'Yelela Code',
  // As duas partes do logótipo, tal como ele as compõe. Separadas porque têm
  // tamanho, cor e tipo diferentes; juntas para leitura, em `app.nome`.
  'app.marcaNome': 'yelela',
  'app.marcaCode': '<code>',
  'app.titulo': 'Yelela Code',
  'app.subtitulo': 'Lições de três minutos, no telemóvel, sem gastar dados.',

  'demo.activo': 'Modo de demonstração: todas as lições estão desbloqueadas. Não existe na aplicação publicada.',
  'demo.desligar': 'Desligar demonstração',
  'demo.ligar': 'Desbloquear todas as lições',
  'demo.explicacao': 'Modo de demonstração, para rever conteúdo sem cumprir os pré-requisitos.',

  // A conta de acesso total (migração 20260809000100). É o que o modo de
  // demonstração acima não pode ser: existe na aplicação publicada. Anunciado
  // sempre, e sem botão para desligar — não é uma preferência deste aparelho,
  // é uma propriedade da conta. A segunda frase está aqui de propósito: sem
  // ela, «tudo desbloqueado» lê-se como uma subscrição paga.
  'acessoTotal.activo': 'Conta de revisão: todas as lições estão abertas, sem cumprir os pré-requisitos.',
  'acessoTotal.naoEPagamento': 'É uma definição desta conta para rever conteúdo. Não é uma subscrição e não substitui nenhum pagamento.',

  // Conta (ADR 0009, bloco 2). O texto de sucesso do registo é verdadeiro
  // exista ou não a conta — é o que H-010 exige. Erros sem exclamação e
  // sempre com saída.
  'conta.titulo': 'A tua conta',
  'conta.semSessao': 'Não estás em nenhuma conta neste aparelho.',
  'conta.nome': 'Nome',
  'conta.email': 'Email',
  'conta.senha': 'Senha',
  'conta.senha.dica': 'Pelo menos 8 caracteres. Uma frase curta serve bem.',
  'conta.idade': 'Idade',
  'conta.idade.dica': 'Em anos. Abaixo dos 13, o registo precisa do encarregado de educação.',
  'conta.encarregado': 'Nome do encarregado de educação',
  'conta.encarregado.declaracao':
    'Autorizo, como encarregado(a) de educação, a criação desta conta e o uso dos dados do estudante para aprender na Yelela Code. (Versão de 2026-09-03.)',
  'conta.sair': 'Sair da conta',
  'conta.porConfirmar': 'O teu email ainda não está confirmado. Para estudar não faz falta — só vai ser preciso antes do primeiro pagamento.',
  'conta.voltarLicoes': 'Voltar às lições',
  'conta.nav.aria': 'Conta',
  'conta.entrar.titulo': 'Entrar',
  'conta.entrar.accao': 'Entrar',
  'conta.entrar.semConta': 'Ainda não tens conta?',
  'conta.entrar.irRegistar': 'Criar conta',
  'conta.registar.titulo': 'Criar conta',
  'conta.registar.accao': 'Criar conta',
  'conta.registar.jaTem': 'Já tens conta?',
  'conta.registar.irEntrar': 'Entrar',
  'conta.registar.feito': 'Registo recebido. Já podes entrar com o teu email e a tua senha.',
  'conta.registar.confirmacaoDepois': 'A confirmação do email fica para depois — só é precisa antes do primeiro pagamento.',
  'conta.confirmar.titulo': 'Confirmar email',
  'conta.confirmar.aConfirmar': 'A confirmar…',
  'conta.confirmar.feito': 'Email confirmado. Está feito.',
  'conta.confirmar.jaUsado': 'Esta ligação já tinha sido usada — o teu email já está confirmado.',
  'conta.confirmar.invalido': 'Esta ligação já não serve. Podes pedir uma nova na tua conta quando precisares dela.',
  'conta.erro.credenciais': 'O email ou a senha não estão certos. Vê se escreveste bem e tenta outra vez.',
  'conta.erro.tentativas': 'Demasiadas tentativas seguidas. Espera uns minutos e tenta outra vez.',
  'conta.erro.contaDePainel':
    'Esta conta é da administração e não entra na zona de cursos. Para gerir, entra pelo painel; para estudar, cria uma conta de estudante com outro email.',

  // Sincronização (ADR 0009, bloco 3). O pendente nunca é apresentado como
  // perda nem como falha — é trabalho guardado à espera de rede.
  'painel.porSincronizar': '{n} por sincronizar — sobe sozinho quando houver rede.',
  'conta.sair.pendentes': 'Tens {n} coisas por sincronizar e não há rede. Se saíres agora, ficam guardadas neste aparelho e sobem quando voltares a entrar aqui.',
  'conta.sair.mesmoAssim': 'Sair na mesma',
  'conta.sair.cancelar': 'Ficar',

  // Era «O teu progresso» até 05/09/2026; o nome passou para o bloco do
  // catálogo que lista os cursos começados (ADR 0027), e dois blocos com o
  // mesmo nome acessível na mesma página são um só para quem ouve.
  'painel.aria': 'Pontos, sequência e objectivo diário',
  'painel.pontos': 'pontos no total',
  'painel.sequencia': 'dias seguidos',
  'painel.licoes': 'lições concluídas',
  'painel.objectivoLegenda': 'Objectivo diário',
  'painel.objectivoFeito': 'Objectivo de hoje cumprido',
  // Minutos e não pontos desde 15/08/2026: o objectivo é em minutos e passou a
  // medir-se em minutos. Arredonda para cima, para nunca dizer «faltam 0» a
  // quem ainda não chegou lá.
  'painel.objectivoFalta': 'Faltam {n} min hoje',
  'painel.minutos': '{n} min',



  // Ecrã principal (ADR 0027): «O teu progresso» só aparece quando há algum
  // curso ou percurso começado — um título sobre uma lista vazia é ruído.
  'catalogo.trilhas': 'Cursos',
  // A etiqueta do cartão de curso e do cartão de progresso.
  'catalogo.trilhaEtiqueta': 'Curso',
  // A sigla no quadrado da linguagem, nos cartões de curso e de progresso e
  // no cabeçalho da página do curso. É a grafia pública da linguagem, não uma
  // abreviatura nossa — por isso não se traduz nem muda.
  'linguagem.sigla.javascript': 'JS',
  'linguagem.sigla.html': 'HTML',
  'linguagem.sigla.css': 'CSS',
  'linguagem.sigla.python': 'PY',
  'linguagem.sigla.sql': 'SQL',
  'catalogo.voltar': 'Voltar ao início',

  // Uma trilha ainda sem lições diz o que é, e não que está avariada.
  'trilha.estado.em-construcao': 'Em construção',
  'trilha.estado.planeada': 'Vem a seguir',
  'trilha.estado.publicada': 'Disponível',
  'trilha.aria.espera': '{titulo}. Ainda não tem lições.',

  // `modulo.progresso` e `modulo.progressoCurto` saíram a 15/08/2026; os
  // textos do caminho de lições (`trilho.*`) e da lista de módulos do
  // catálogo saíram a 05/09/2026 com a página do curso (ADR 0027), que tem os
  // seus em `curso.*`.
  // Sem minutos desde 15/08/2026: a lição deixou de declarar tempo. O único
  // tempo do produto é o objectivo diário, que o estudante escolhe.
  'modulo.aria.abrir': '{titulo}. {feitas} de {total} lições concluídas. Curso {trilha}.',
  'modulo.inexistente': 'Este módulo já não existe.',

  // O fim do caminho. Sem isto a página acaba sem dizer nada, e quem quer
  // continuar tem de voltar ao catálogo e descobrir sozinho qual é o seguinte.
  'proximo.fimEtiqueta': 'Fim do curso',
  'proximo.fimTexto': 'Chegaste ao último módulo de {trilha}. Não há mais lições por agora.',

  // Só a consolidação é etiquetada. Etiquetar também a matéria punha uma
  // etiqueta em cada linha, e aí deixaria de assinalar seja o que for.
  'trilho.consolidacao': 'Prática',
  'trilhas.vazio': 'Ainda não começaste nenhum curso.',
  'trilhas.precisaDe': 'Disponível depois de concluíres: {licoes}.',
  'licao.bloqueada.titulo': 'Esta lição ainda não está disponível.',
  // Sair de uma lição devolve ao módulo dela. O texto diz para onde se vai —
  // "voltar às trilhas" mandava para um sítio que já não é o de partida.
  'licao.bloqueada.accao': 'Voltar ao curso',
  'trilhas.erro.titulo': 'Não foi possível carregar as lições.',
  'trilhas.erro.accao': 'Tentar outra vez',
  'trilhas.acarregar': 'A carregar as lições.',

  'licao.sair': 'Sair da lição',
  // H-299 — o interruptor dos sons da lição. O rótulo diz o estado **e** a
  // acção, porque um botão de nota musical sozinho não diz qual dos dois é.
  'som.ligado': 'Sons ligados. Tocar para desligar.',
  'som.desligado': 'Sons desligados. Tocar para ligar.',
  'licao.sairConfirmar': 'Sair da lição? O que fizeste até aqui fica guardado.',
  'licao.verificar': 'Verificar',
  'licao.continuar': 'Continuar',
  'licao.tentar': 'Tentar outra vez',
  'licao.verDica': 'Ver dica',
  'licao.verDicaCusta': 'Ver dica (−{n} pontos)',
  'licao.dicaCusta': 'A dica seguinte custa {n} pontos.',
  // H-121 — o desafio anuncia-se: sem esqueleto, dicas mais caras.
  'licao.desafio.etiqueta': 'Desafio',
  'licao.desafio.nota': 'Sem esqueleto — escreves do zero. Aqui cada dica custa {n} pontos.',
  'licao.dicaSim': 'Ver mesmo assim',
  'licao.dicaNao': 'Agora não',
  'licao.resolvidoComAjuda': 'Este exercício fica marcado como resolvido com ajuda. Refá-lo mais tarde sem a solução à vista.',
  'licao.reiniciar': 'Reiniciar o exercício',
  'licao.acorrer': 'A verificar…',
  // Só visível em ecrã grande, onde há teclado físico (ADR 0005).
  'licao.atalhoVerificar': 'Ctrl+↵',
  'licao.certo': 'Certo.',
  'licao.aindaNao': 'Ainda não.',
  'licao.pontosGanhos': '+{n} pontos',
  'licao.esperado': 'esperava',
  'licao.obtido': 'obteve',
  'licao.consola': 'Saída do programa',
  'licao.previa': 'A tua página',
  'licao.paginaDada': 'A página que te é dada',
  'licao.paginaDadaNota': 'Esta parte não se altera. O que escreves é o estilo dela.',
  'licao.consolaVazia': 'Ainda não correste o código.',
  'licao.consolaErro': 'O programa parou aqui',
  'licao.dicaTitulo': 'Dica {n} de {total}',
  'licao.semDicas': 'Não há mais dicas para este exercício.',
  'licao.dicaAutomatica': 'Aqui vai uma dica, sem custo.',
  'licao.editorAria': 'Editor de código',
  'licao.corpoAria': 'Exercício',
  'licao.respostaTexto': 'A tua resposta',
  'licao.respostaDica': 'Escreve exactamente o que o programa mostra.',
  // ADR 0026 — o `setup` de um exercício de SQL, mostrado em leitura apenas:
  // é a tabela contra que a consulta do estudante corre.
  'licao.setupSql': 'A base de dados deste exercício',
  // A explicação da lição (`intro` do licao.yml). O mesmo rótulo serve de
  // texto do botão que abre/fecha — o estado em si é anunciado por
  // `aria-expanded`, não por uma segunda frase.
  'licao.intro.titulo': 'Explicação da lição',
  'licao.intro.continuar': 'Ler o resto da explicação',

  'licao.concluida.titulo': 'Lição concluída.',
  'licao.concluida.pontos': '+{n} pontos',
  'licao.concluida.seguinte': 'Voltar ao curso',
  'licao.concluida.resumo': '{acertos} de {total} exercícios à primeira tentativa.',
  // RN-007 — o caminho de volta: refazer sem a solução à vista. Não é falha;
  // é o que falta para o certificado, dito com a saída ao lado.
  'licao.concluida.comAjuda.um': 'Um exercício ficou resolvido com a solução à vista. Para contar para o certificado, refaz a lição sem abrir a terceira dica nesse exercício.',
  'licao.concluida.comAjuda.varios': '{n} exercícios ficaram resolvidos com a solução à vista. Para contarem para o certificado, refaz a lição sem abrir a terceira dica nesses exercícios.',
  'licao.concluida.refazer': 'Refazer a lição',

  'exercicio.escolhaAria': 'Opções de resposta',
  'exercicio.lacunaAria': 'Lacuna {n} de {total}',
  'exercicio.blocosAria': 'Blocos de código, por ordem',
  'exercicio.subir': 'Mover para cima: {bloco}',
  'exercicio.descer': 'Mover para baixo: {bloco}',
  'exercicio.termosAria': 'Termos',
  'exercicio.definicoesAria': 'Definições',
  'exercicio.ligarA': 'Ligar ao termo seleccionado: {definicao}',
  'exercicio.ligadoA': 'Ligado ao termo {letra}: {definicao}. Premir para desligar.',

  // A barra da lição não tem rótulo visível desde 15/08/2026 — o `exercicio.contador`
  // que aqui vivia dizia «1/6» e saiu. Este `aria-label` passou a ser o único
  // texto da barra, e é por isso que tem de continuar a existir.
  'progresso.aria': 'Progresso na lição',

  'erro.semLigacao': 'Sem ligação. Podes continuar nas lições que já descarregaste.',
  'erro.tempoExcedido': 'O teu código demorou demasiado tempo. Verifica se há um ciclo que nunca termina.',
  // O código não chegou a correr. Não se acusa o estudante de nada, porque não
  // há nada de que o acusar: a culpa não é do código dele.
  'erro.naoArrancou': 'Não foi possível correr o teu código: o motor não chegou a carregar. Verifica a ligação e tenta outra vez — o que escreveste fica guardado.',
  'erro.sintaxe': 'O código tem um erro de escrita e não chega a correr.',
  'erro.detalheTecnico': 'Ver detalhe técnico',

  'simbolos.aria': 'Símbolos de {linguagem}',
  'simbolos.indentar': 'Indentar',

  // ---------------------------------------------------------------------------
  // A descarga do Python (ADR 0017, decisão 5.3)
  //
  // Este é o texto com mais peso ético da aplicação: pede megabytes de dados
  // pré-pagos a alguém que pode ter 50 MB para o dia inteiro. Três regras, e
  // nenhuma é estética.
  //
  // 1. **Os dois botões têm o mesmo peso.** Não há «Descarregar» grande e
  //    «Mais tarde» em cinzento pequeno. Um produto que gasta dados de alguém
  //    não os pede com um padrão escuro.
  // 2. **O adiamento é real.** Quem disser «mais tarde» continua a usar a
  //    trilha, e o texto diz-lhe exactamente o que consegue fazer — não é uma
  //    promessa vaga para o fazer sentir menos mal por recusar.
  // 3. **O tamanho vem de uma constante medida no build**
  //    (`src/runtime/python/versao.ts`), nunca escrito à mão numa frase: uma
  //    frase com um número desactualizado é uma mentira que ninguém decidiu
  //    contar.
  //
  // Não se mostra preço. O ADR propunha kwanzas; não há dados de mercado
  // fiáveis, e um preço errado no ecrã é pior do que nenhum. Fica como questão
  // aberta para o dono do produto.
  // ---------------------------------------------------------------------------
  'python.pedir.titulo': 'Este exercício corre Python no teu telemóvel.',
  'python.pedir.oQueE': 'Para isso é preciso descarregar o Python uma vez. São cerca de {mb} MB.',
  // Diz «não se paga outra vez» e **não** «funciona sem rede nenhuma». A
  // segunda seria falsa hoje: o Python fica cá, mas a própria aplicação ainda
  // precisa de rede para abrir enquanto H-060 (o service worker) não existir.
  // Prometer offline completo antes de ele existir seria a aplicação mentir
  // sobre a única coisa que o estudante está a comprar com os megabytes.
  'python.pedir.umaVez': 'Fica guardado neste aparelho: das próximas vezes o Python já cá está e não se descarrega outra vez.',
  'python.pedir.dados': 'É bastante para dados móveis. Se puderes, faz isto numa ligação Wi-Fi.',
  'python.pedir.ligacaoLenta': 'A tua ligação parece lenta neste momento. Numa ligação assim, isto pode demorar vários minutos.',
  'python.pedir.seEsperares': 'Se preferires esperar, continuas no curso à mesma: as perguntas de escolha, as lacunas, os blocos e o prever o resultado não precisam disto. Só os exercícios de escrever código.',
  'python.pedir.sim': 'Descarregar',
  'python.pedir.naoAgora': 'Mais tarde',

  // São **duas** esperas e o estudante vê as duas, anunciadas desde o início.
  // Uma barra que chega aos 100 % e deixa mais quinze segundos de ecrã parado
  // lê-se como avaria — e é o momento em que ele acabou de gastar 6,4 MB, o
  // pior momento possível para a aplicação parecer avariada.
  'python.descarga.titulo': 'A descarregar o Python.',
  'python.descarga.passo': 'Passo 1 de 2 · a descarregar',
  'python.descarga.progresso': '{pct}% de cerca de {mb} MB',
  'python.descarga.retomavel': 'Podes sair. O que já entrou fica guardado e não se paga outra vez.',
  'python.preparar.titulo': 'A preparar o Python.',
  'python.preparar.passo': 'Passo 2 de 2 · a preparar',
  'python.preparar.semRede': 'Já não é rede: são os ficheiros que descarregaste a serem lidos. Na primeira vez pode ir a meio minuto num telemóvel mais lento.',

  // Sem drama e com saída, como qualquer outro erro da casa. A segunda frase é
  // a que interessa: sem ela, o estudante presume que perdeu os dados que já
  // gastou e não tenta outra vez.
  'python.erro.titulo': 'A descarga parou.',
  'python.erro.retoma': 'O que já tinha entrado ficou guardado. Continuar não volta a pagar essa parte.',
  'python.erro.continuar': 'Continuar a descarga',
  'python.erro.semArmazenamento': 'Este navegador não deixa guardar a descarga, e sem isso o Python teria de ser descarregado outra vez em cada visita. Podes continuar nos exercícios que não correm código.',

  // Depois de o estudante ter dito «mais tarde», o exercício de código mostra
  // isto em vez de um editor que não corre.
  'python.adiado.titulo': 'Este exercício precisa do Python.',
  'python.adiado.explicacao': 'Disseste «mais tarde», e continua a valer. Podes descarregar agora ou passar à frente.',
  'python.adiado.passar': 'Passar à frente',

  // O arranque do interpretador, depois de a descarga estar feita. Já não é
  // rede: são os bytes que estão no aparelho a serem lidos e compilados.
  //
  // Isto tem de ser dito, e medimo-lo: num aparelho modesto o **primeiro**
  // arranque é da ordem dos quinze segundos. Quinze segundos de ecrã parado
  // são indistinguíveis de avaria, e o estudante fecha a lição a meio a pensar
  // que a aplicação encravou. A segunda frase é a que evita isso — e é
  // verdade: a carga seguinte pede 261 bytes ao servidor e demora menos de
  // dois segundos.
  'python.arrancar.motor': 'A preparar o Python…',
  'python.arrancar.interpretador': 'A arrancar o Python…',
  'python.arrancar.demora': 'A primeira vez demora; num telemóvel mais lento pode ir a meio minuto. Das próximas é bem mais rápido, e sem gastar dados. Podes ir escrevendo.',
  // RF-023: dez segundos, e o corte mata o interpretador — a execução seguinte
  // paga um arranque novo, mas o separador fica vivo.
  'python.erro.tempoExcedido': 'O teu código demorou mais de 10 segundos. Verifica se há um ciclo que nunca termina.',
  // ADR 0017, decisão 5.10: em Python a causa mais provável não é a rede, é
  // memória. As duas mandam olhar para sítios diferentes.
  'python.erro.naoArrancou': 'O Python não chegou a arrancar neste aparelho. Costuma ser falta de memória: fecha outros separadores e outras aplicações e tenta outra vez. O que escreveste fica guardado.',
  // -------------------------------------------------------------------------
  // H-202 — preços e pagamentos por transferência. Este é o percurso onde o
  // estudante já entregou dinheiro e ainda não tem nada em troca: cada texto
  // diz com precisão em que estado o pedido está e o que acontece a seguir
  // (skill pagamento-transferencia). Nunca se sugere confirmação antes de a
  // haver, e o erro nunca acusa.
  // -------------------------------------------------------------------------
  'precos.titulo': 'Preços',
  'precos.descricao': 'Pagas por transferência bancária e nós confirmamos. Sem cartão e sem subscrição escondida.',
  'precos.semOfertas': 'Ainda não há nada à venda por aqui. As lições abertas continuam abertas.',
  'precos.comprar': 'Pagar por transferência',
  'precos.precisaDeConta': 'Para pagar precisas de estar na tua conta — é a ela que o acesso fica ligado.',
  'precos.aCriar': 'A preparar o pedido…',
  'precos.condicao.institucional': 'Preço da tua instituição',
  'precos.condicao.campanha': 'Preço de campanha',
  // H-304 — a página agrupada pelo que se vende, com o que já está pago à vista.
  'precos.seccao.cursos': 'Cursos',
  'precos.seccao.cursos.descricao': 'Um curso de cada vez. Pagas uma vez e o acesso fica ligado à tua conta.',
  'precos.seccao.niveis': 'Percurso, nível a nível',
  'precos.seccao.niveis.descricao': 'O percurso tem três níveis, cada um com o seu projecto. Podes pagar um de cada vez, pela ordem.',
  'precos.seccao.percurso': 'Percurso completo',
  'precos.seccao.percurso.descricao': 'Os três níveis de uma vez — sai mais barato do que um a um.',
  'precos.seccao.programas': 'Programas',
  'precos.seccao.programas.descricao': 'Acesso pelo programa da tua instituição.',
  'precos.ambito.track': 'Curso',
  'precos.ambito.level': 'Nível {n}',
  'precos.ambito.path': 'Percurso completo',
  'precos.ambito.program': 'Programa',
  'precos.inclui.track': 'Todas as secções e tópicos do curso, com certificado no fim.',
  'precos.inclui.level': 'As lições do nível e o projecto integrador, com certificado de nível.',
  'precos.inclui.path': 'Os três níveis, os três projectos e o certificado do percurso.',
  'precos.inclui.program': 'As turmas e o acompanhamento do programa.',
  'precos.verCurso': 'Ver o que o curso inclui',
  'precos.verPercurso': 'Ver o percurso',
  'precos.irParaCurso': 'Ir para o curso',
  'precos.irParaPercurso': 'Ir para o percurso',
  'precos.precoPublico': 'preço público {preco}',
  'precos.entrarParaPagar': 'Entrar para pagar',
  'precos.emDestaque': 'Continua aqui — a oferta que escolheste.',
  'acesso.pago': 'Pago',
  'acesso.pagoAria': 'Pago — o acesso está activo',
  'acesso.jaTens': 'Já tens acesso',
  'acesso.subscricao': 'Tens uma subscrição activa ({plano}) até {ate}: todo o conteúdo está aberto.',

  'pagamentos.titulo': 'Pagamentos',
  'pagamentos.nenhum': 'Não tens pedidos de pagamento neste momento.',
  'pagamentos.nenhumActivo': 'Não tens nenhum pagamento a decorrer.',
  'pagamentos.activos': 'Pagamentos a decorrer',
  'pagamentos.historico': 'Histórico ({n})',
  'pagamentos.verPrecos': 'Ver os preços',
  'pagamentos.transfere': 'Transfere para:',
  'pagamentos.empresa.nif': 'NIF {nif}',
  'pagamentos.numero': 'Número',
  'pagamentos.qualquerCanal': 'Serve qualquer um destes meios — o montante e a referência são os mesmos.',
  'metodo.transferencia_bancaria': 'Transferência bancária',
  'metodo.multicaixa_express': 'Multicaixa Express',
  'metodo.unitel_money': 'Unitel Money',
  'metodo.afrimoney': 'Afrimoney',
  'metodo.numerario': 'Numerário, no escritório',
  'pagamentos.numerario.dica': 'Leva a referência contigo — pagas ao balcão e o acesso fica activo na hora.',
  'pagamentos.titular': 'Titular',
  'pagamentos.banco': 'Banco',
  'pagamentos.iban': 'IBAN',
  'pagamentos.montante': 'Montante',
  'pagamentos.referencia': 'Referência',
  'pagamentos.copiar': 'Copiar {campo}',
  'pagamentos.copiado': 'Copiado.',
  'pagamentos.avisoReferencia': 'Escreve a referência {referencia} na descrição da transferência. Sem ela, a confirmação pode demorar mais.',
  'pagamentos.validoAte': 'Válido até {data}.',
  'pagamentos.carregar': 'Já transferi — carregar comprovativo',
  'pagamentos.carregar.dica': 'Uma fotografia nítida do comprovativo, ou o PDF do banco. Até 5 MB.',
  'pagamentos.aEnviar': 'A enviar o comprovativo…',
  'pagamentos.outroComprovativo': 'Carregar outro comprovativo',

  // Um estado por linha, sempre com o passo seguinte. O prazo de 24 h da
  // verificação é promessa do produto (skill), não estimativa solta.
  'pagamentos.estado.criado': 'À espera da tua transferência.',
  'pagamentos.estado.comprovativo_submetido': 'Recebemos o teu comprovativo. Vamos confirmar até 24 horas.',
  'pagamentos.estado.em_verificacao': 'O teu comprovativo está a ser verificado.',
  'pagamentos.estado.aprovado': 'Pagamento confirmado. O teu acesso está activo.',
  'pagamentos.estado.rejeitado': 'Precisamos de mais uma coisa:',
  'pagamentos.estado.expirado': 'Este pedido expirou sem comprovativo. Podes criar um novo na página de preços.',

  'pagamentos.erro.duplicado': 'Este ficheiro já foi usado noutro pagamento. Se transferiste mais do que uma vez, carrega o comprovativo dessa transferência específica.',
  'pagamentos.erro.invalido': 'O ficheiro tem de ser uma imagem (JPG ou PNG) ou o PDF do banco.',
  'pagamentos.erro.grande': 'O ficheiro passa dos 5 MB. Uma fotografia em qualidade normal chega bem.',
  'pagamentos.erro.rede': 'Não conseguimos enviar agora. O ficheiro fica no teu aparelho — tenta outra vez quando houver rede.',
  'pagamentos.erro.criar': 'Não conseguimos preparar o pedido agora. Tenta outra vez daqui a um bocado; se continuar, fala connosco.',
  'conta.pagamentos': 'Os meus pagamentos',
  'conta.mensagens': 'As minhas mensagens',

  // -------------------------------------------------------------------------
  // E14 — o catálogo híbrido: cursos e percursos lado a lado (ADR 0019).
  // «Curso» e «trilha» são a mesma coisa vista de dois lados; um percurso é
  // um roteiro com níveis por cima do mesmo conteúdo — a etiqueta e o
  // microtexto existem para essa diferença se perceber sem explicação.
  // -------------------------------------------------------------------------
  'catalogo.percursos': 'Percursos',

  // O herói de quem chega sem progresso (à Mimo: uma frase, um CTA, zero
  // ruído). Quem já estudou não o vê — a pergunta dele é «onde é que eu ia»,
  // e essa responde-se com o Continuar.
  // E22 — o dashboard de quem volta.
  'continuar.etiqueta': 'Continuar onde ias',
  'continuar.cta': 'Continuar em {modulo}',
  // H-223 — o bloco da turma, só para quem tem programa institucional.
  'turma.titulo': 'A tua turma',
  'turma.programa': 'Programa',
  'turma.tutores': 'Tutores',
  'turma.nivel': 'Nível actual',
  'turma.proximaSessao': 'Próxima sessão presencial',
  'turma.sessao': '{data} · {local} — {tema}',

  // E17 — a área do tutor. Entra pela porta do estudante; a atribuição é a
  // única chave, e sem turmas a página di-lo sem culpa.
  'tutor.titulo': 'As tuas turmas',
  'tutor.nav': 'Turmas',
  'tutor.vazio': 'Não tens turmas atribuídas. A atribuição é feita no painel de administração — sem ela, e de propósito, não se vê nada.',
  'tutor.estudantes': 'Estudantes',
  'tutor.semEstudantes': 'Ainda sem estudantes inscritos.',
  'tutor.licoes': '{n} lições concluídas',
  'tutor.estado.por_comecar': 'Por começar',
  'tutor.estado.a_decorrer': 'A decorrer',
  'tutor.estado.suspensa': 'Suspensa',
  'tutor.estado.concluida': 'Concluída',
  'tutor.estado.cancelada': 'Cancelada',

  // A barra de topo persistente (à Mimo).
  'barra.aria': 'Navegação principal',
  'barra.inicio.aria': 'Yelela Code — voltar ao início',
  'barra.conta.aria': 'A tua conta',
  'barra.mensagens.aria': 'Mensagens',
  'barra.mensagens.porLer': 'Mensagens: {n} por ler',

  // ADR 0028 — a caixa de mensagens. As de sistema não trazem texto do
  // servidor: o tipo e os dados chegam, e a frase nasce aqui.
  'mensagens.titulo': 'Mensagens',
  'mensagens.aCarregar': 'A carregar as mensagens…',
  'mensagens.vazio': 'Ainda não tens mensagens. Quando um pagamento for confirmado ou um projecto for revisto, aparece aqui.',
  'mensagens.nova': 'Nova',
  'mensagens.tipo.pagamento': 'Pagamento',
  'mensagens.tipo.projecto': 'Projecto',
  'mensagens.tipo.aviso': 'Aviso',
  'mensagens.abrir.pagamento': 'Ver os meus pagamentos',
  'mensagens.abrir.projecto': 'Ver o percurso',
  'mensagens.abrir.aviso': 'Abrir',
  'mensagens.pagamento.aprovado.titulo': 'Pagamento confirmado — {referencia}',
  'mensagens.pagamento.aprovado.corpo': 'Recebemos a tua transferência de {montante}. O teu acesso está activo — bom estudo.',
  'mensagens.pagamento.rejeitado.titulo': 'Precisamos de mais uma coisa — {referencia}',
  'mensagens.pagamento.rejeitado.corpo': 'Não conseguimos confirmar o comprovativo do pedido {referencia}. Abre os teus pagamentos para ver o que corrigir e voltar a carregar.',
  'mensagens.pagamento.em_verificacao.titulo': 'O teu comprovativo está a ser verificado — {referencia}',
  'mensagens.pagamento.em_verificacao.corpo': 'Já estamos a conferir a transferência de {montante}. Avisamos-te aqui assim que estiver decidido.',
  'mensagens.projecto.aprovado.titulo': 'Projecto aprovado: {projecto}',
  'mensagens.projecto.aprovado.corpo': 'O tutor aprovou a tua entrega. O nível fica concluído quando as lições estiverem todas feitas.',
  'mensagens.projecto.revisao_necessaria.titulo': 'O teu projecto precisa de revisão: {projecto}',
  'mensagens.projecto.revisao_necessaria.corpo': 'O tutor deixou comentários na tua entrega. Lê o feedback no percurso e envia uma versão nova.',

  'hero.titulo': 'Aprende a programar, uma lição de cada vez',
  'hero.texto': 'Lições de um minuto que cabem no teu dia. No telemóvel que já tens, mesmo sem rede.',
  'hero.cta': 'Começar agora — é grátis',
  'hero.aria': 'Começar a primeira lição de {curso}',
  'percurso.etiqueta': 'Percurso',
  'percurso.niveis': '{n} níveis',
  // A contagem vai no aria, não no ecrã: a barra é que cresce (regra 11).
  'percurso.aria.abrir': 'Abrir o percurso {titulo}: {n} níveis, {feitas} de {total} lições concluídas',
  'percurso.aria.progresso': 'Progresso do percurso',
  'percurso.nivel.aria.progresso': 'Progresso de {titulo}: {feitas} de {total} lições',
  'percurso.projecto.futuro': 'Cada nível vai fechar com um projecto avaliado por um tutor. Ainda não está aberto — as lições contam desde já.',
  // Avisa e não impede (ADR 0015 decisão 6, mantida para níveis): o cartão
  // fechado diz porquê, diz como abre, e deixa entrar.
  'percurso.nivel.fechado': 'Sugerimos terminar «{anterior}» primeiro. Podes entrar na mesma — a ordem é a nossa recomendação, não uma porta.',
  'percurso.nivel.completo': 'Nível concluído',
  'percurso.vazio': 'Este percurso ainda não tem conteúdo publicado.',
  'percurso.voltar': 'Voltar ao catálogo',
  // E12 — o projecto integrador conta na barra (peso configurável) e é
  // condição de conclusão (RN-011). A contagem continua no aria, não no ecrã.
  'percurso.nivel.aria.progressoComProjecto': 'Progresso de {titulo}: {feitas} de {total}, contando o projecto integrador',
  'percurso.projecto.regra': 'Cada nível fecha com um projecto integrador, revisto por um tutor. O nível fica concluído com todas as lições feitas e o projecto aprovado.',

  'projecto.etiqueta': 'Projecto integrador',
  'projecto.abrir': 'Ver o enunciado e entregar',
  'projecto.fechar': 'Recolher o projecto',
  'projecto.criterios': 'O que o tutor vai olhar',
  'projecto.estado.porEntregar': 'Por entregar',
  'projecto.estado.entregue': 'Versão {versao} entregue — à espera de revisão',
  'projecto.estado.rever': 'Versão {versao} revista — o tutor deixou-te o passo seguinte',
  'projecto.estado.aprovado': 'Aprovado',
  // Regra 8: o feedback é o passo seguinte, nunca uma reprovação.
  'projecto.feedback.aprovado': 'O que o tutor escreveu',
  'projecto.feedback.rever': 'O que o tutor te pede para a próxima versão',
  'projecto.semSessao': 'Para entregar o projecto precisas de entrar na tua conta — é assim que o tutor sabe de quem é.',
  'projecto.aguarda': 'A tua entrega está com o tutor. Quando a revisão chegar, aparece aqui, com o feedback.',
  'projecto.concluido': 'Projecto aprovado. Este nível conta como concluído assim que as lições estiverem todas feitas.',
  'projecto.campo.ligacao': 'Ligação ao teu projecto',
  'projecto.campo.texto': 'O teu código',
  'projecto.dica.ligacao': 'Um repositório público ou a página publicada. Confirma que abre numa janela anónima antes de entregar.',
  'projecto.dica.texto': 'Cola o programa inteiro. Podes entregar outra vez depois do feedback — a versão anterior não se perde.',
  'projecto.entregar': 'Entregar ao tutor',
  'projecto.entregarOutraVez': 'Entregar a nova versão',
  'projecto.erro.ligacao': 'Essa ligação não parece completa. Começa por https:// e confirma que abre.',
  'projecto.erro.repetida': 'Já há uma entrega tua à espera de revisão. Quando o tutor responder, podes entregar outra vez.',

  // H-173/H-175 — a fila do tutor e a revisão com feedback.
  'tutor.fila.titulo': 'Projectos por rever',
  'tutor.fila.vazia': 'Nenhum projecto à espera. Quando um estudante das tuas turmas entregar, aparece aqui.',
  'tutor.fila.versao': 'versão {versao}',
  'tutor.fila.entregueEm': 'entregue a {data}',
  'tutor.fila.abrirLigacao': 'Abrir a entrega numa janela nova',
  'tutor.fila.verCodigo': 'Ver o código entregue',
  'tutor.fila.criterios': 'Critérios do projecto',
  'tutor.fila.feedback': 'Feedback para o estudante',
  'tutor.fila.feedbackDica': 'O estudante lê isto tal como está. Diz o que está bem, o que falta, e por onde começar — nunca só «errado».',
  'tutor.fila.aprovar': 'Aprovar',
  'tutor.fila.pedirRevisao': 'Pedir nova versão',
  'tutor.fila.erro': 'A revisão não foi registada. Confirma o feedback (pelo menos 10 caracteres) e tenta outra vez.',
  'tutor.fila.feito': 'Revisão registada. O estudante vê o feedback na página do percurso.',

  // E21 — certificados. A regra diz-se por extenso e não se negoceia (RN-003,
  // RN-007, RN-011); o que falta diz-se sempre, com a saída ao lado.
  'conta.certificados': 'Os meus certificados',
  'certificados.titulo': 'Certificados',
  'certificados.regra': 'Um certificado emite-se com todos os exercícios feitos, nenhum resolvido com a solução à vista por refazer, e — nos níveis e no percurso — o projecto integrador aprovado. Sem excepções, e sem atalhos por pagamento.',
  'certificados.emitidos': 'Emitidos',
  'certificados.porObter': 'Por obter',
  'certificados.tipo.curso': 'Curso',
  'certificados.tipo.nivel': 'Nível',
  'certificados.tipo.percurso': 'Percurso',
  'certificados.emitidoEm': 'Emitido a {data}',
  'certificados.revogado': 'Revogado',
  'certificados.abrir': 'Ver e imprimir',
  'certificados.elegivel': 'Tudo feito. Podes emitir o certificado.',
  'certificados.emitir': 'Emitir certificado',
  'certificados.falta.licao': 'Falta 1 lição',
  'certificados.falta.licoes': 'Faltam {n} lições',
  'certificados.falta.refazer': '1 exercício por refazer sem ajuda',
  'certificados.falta.refazerVarios': '{n} exercícios por refazer sem ajuda',
  'certificados.falta.projecto': 'Projecto integrador por aprovar',
  'certificados.aria.progresso': 'Progresso para o certificado de {titulo}: {feitas} de {total}',
  'certificados.erro.aindaNao': 'Ainda falta qualquer coisa — a lista acima está actualizada. Volta a tentar quando estiver tudo feito.',
  'certificados.erro.semNome': 'O certificado leva o teu nome, e o perfil ainda não o tem. Preenche-o na conta e volta aqui.',
  'certificados.voltar': 'Voltar à conta',

  'verificar.titulo': 'Verificar certificado',
  'verificar.inexistente': 'Não existe nenhum certificado com o código {codigo}. Confirma os dez caracteres — o código não tem I, O, 0 nem 1.',
  'verificar.valido': 'Certificado válido, emitido pela Yelela.',
  'verificar.revogado': 'Este certificado foi revogado e já não é válido.',
  'certificado.etiqueta': 'Certificado de conclusão',
  'certificado.texto.curso': 'concluiu com aproveitamento o curso',
  'certificado.texto.nivel': 'concluiu com aproveitamento o nível',
  'certificado.texto.percurso': 'concluiu com aproveitamento o percurso',
  'certificado.regra': 'Todos os exercícios resolvidos sem a solução à vista; nos níveis e percursos, projecto integrador revisto e aprovado por um tutor.',
  'certificado.emitidoEm': 'Emitido a',
  'certificado.codigo': 'Código',
  'certificado.verificacao': 'Verificável em {url}',
  'certificado.imprimir': 'Imprimir ou guardar em PDF',

  // E18 — a área do coordenador. O âmbito diz-se à cabeça (Q9): a sua
  // instituição, progresso e certificados, e nada mais.
  'coordenador.nav': 'Instituição',
  'coordenador.titulo': 'A tua instituição',
  'coordenador.ambito': 'Progresso e certificados dos estudantes da tua instituição. O código que escrevem e as entregas de projecto ficam de fora — são deles.',
  'coordenador.vazio': 'Não estás registado como coordenador de nenhuma instituição. A adesão é feita no painel de administração — sem ela, e de propósito, não se vê nada.',
  'coordenador.semProgramas': 'Ainda sem programas.',
  'coordenador.semTurmas': 'Ainda sem turmas.',
  'coordenador.exportar': 'Exportar CSV',
  'coordenador.ind.estudantes': 'Estudantes',
  'coordenador.ind.mediaLicoes': 'Lições por estudante',
  'coordenador.ind.certificados': 'Certificados',
  'coordenador.turma.resumo': '{n} estudantes · {media} lições por estudante · {certificados} certificados',
  'coordenador.certificadosDoEstudante': '{n} certificado(s)',
  'coordenador.modalidade.adesao_individual': 'Adesão individual',
  'coordenador.modalidade.gestao_institucional': 'Gestão institucional',
  'coordenador.modalidade.licenca_por_vagas': 'Licença por vagas',

  // H-012 — recuperar a palavra-passe. A mesma resposta exista ou não a
  // conta; e a verdade sobre o canal: não há email, há uma pessoa.
  'conta.entrar.esqueci': 'Esqueci-me da palavra-passe',
  'conta.recuperar.titulo': 'Recuperar a palavra-passe',
  'conta.recuperar.explicacao': 'Diz-nos o email da conta. Se existir, preparamos uma ligação para definires uma palavra-passe nova.',
  'conta.recuperar.accao': 'Pedir ligação',
  'conta.recuperar.pedido': 'Pedido recebido. Se houver conta com este email, vais receber uma ligação para definir a palavra-passe nova.',
  'conta.recuperar.comoChega': 'A ligação é entregue pela equipa da Yelela — pelo tutor da tua turma, ou pelo contacto da tua instituição — e vale uma hora. Se não te chegar, pede outra vez.',
  'conta.recuperar.voltar': 'Voltar a entrar',
  'conta.redefinir.titulo': 'Palavra-passe nova',
  'conta.redefinir.nova': 'Palavra-passe nova',
  'conta.redefinir.dica': 'Pelo menos 8 caracteres.',
  'conta.redefinir.repetir': 'Repete a palavra-passe',
  'conta.redefinir.diferentes': 'As duas não são iguais. Confere e escreve outra vez.',
  'conta.redefinir.accao': 'Guardar a palavra-passe',
  'conta.redefinir.feito': 'Palavra-passe guardada. Por segurança, todas as sessões antigas foram terminadas — entra outra vez, aqui e no telemóvel.',
  'conta.redefinir.jaUsado': 'Esta ligação já foi usada. Se não foste tu, pede uma nova.',
  'conta.redefinir.invalido': 'Esta ligação já não serve — valia uma hora. Pede uma nova.',

  // H-013 — perfil.
  'conta.perfil.titulo': 'Perfil',
  'conta.perfil.nome': 'Nome',
  'conta.perfil.nomeDica': 'É o nome que vai para o certificado.',
  'conta.perfil.instituicao': 'Instituição (opcional)',
  'conta.perfil.ano': 'Ano do curso (opcional)',
  'conta.perfil.guardar': 'Guardar',
  'conta.perfil.guardado': 'Perfil guardado.',
  'conta.perfil.erro': 'Não ficou guardado. Confirma o nome (até 60 caracteres) e o ano (1 a 8), e tenta outra vez.',

  // H-014 — os teus dados. Eliminar diz o que apaga e o que fica anónimo,
  // e pede a palavra por escrito.
  'conta.dados.titulo': 'Os teus dados',
  'conta.dados.exportarExplicacao': 'Leva tudo o que é teu: perfil, progresso, submissões, pontos, entregas de projecto, certificados, pedidos de pagamento e mensagens, num ficheiro JSON.',
  'conta.dados.exportar': 'Exportar os meus dados',
  'conta.dados.eliminar': 'Eliminar a conta',
  'conta.dados.eliminarAviso': 'Isto apaga o teu nome, o email, a palavra-passe e as sessões, e não se desfaz. O progresso e as submissões ficam sem dono, anónimos, para as contagens da instituição não mudarem. Se quiseres guardar alguma coisa, exporta primeiro.',
  'conta.dados.eliminarEscreve': 'Escreve ELIMINAR para confirmar',
  'conta.dados.eliminarConfirmar': 'Eliminar a conta definitivamente',

  // ADR 0027 — o curso à imagem da referência: secções, subsecções e tópicos,
  // com a contagem visível em cada nível (decisão do dono do produto,
  // 05/09/2026, que revoga a regra 11 para os ecrãs de navegação — dentro da
  // lição a barra continua a crescer sem número).
  'barra.aprender': 'Aprender',
  'barra.cursos': 'Cursos',
  'catalogo.progresso': 'O teu progresso',
  'catalogo.percentagem': '{n}% concluído',
  // A percentagem curta, para dentro do anel de progresso (ADR 0027). O
  // «concluído» por extenso não cabe no furo do anel e vai no rótulo da
  // ligação, que leva tudo por extenso.
  'catalogo.anel': '{n}%',
  'catalogo.contagem': '{feitas}/{total}',
  'catalogo.curso.aria': '{titulo}: {percentagem}% concluído, {feitas} de {total} secções.',
  'catalogo.percurso.aria': '{titulo}: {percentagem}% concluído, {feitas} de {total} lições.',
  'trilha.nivel.iniciante': 'Para principiantes',
  'trilha.nivel.continuacao': 'Continuação',
  'trilha.aria.curso': 'Abrir o curso {titulo}: {n} secções, {licoes} tópicos. {nivel}.',
  'curso.etiqueta': 'O teu curso',
  'curso.voltar': 'Todos os cursos',
  'curso.inexistente': 'Este curso já não existe.',
  'curso.vazio': 'Este curso ainda não tem secções publicadas.',
  'curso.seccoes': 'Secções',
  'curso.seccoes.aria': 'Secções do curso {curso}, por ordem',
  'curso.seccoes.alternar': 'Secções · {feitas}/{total}',
  'curso.seccao.aria': 'Secção {n}, {titulo}: {feitas} de {total} subsecções concluídas.',
  'curso.seccao.numero': '{n}. {titulo}',
  'curso.continuar': 'Continuar: {topico}',
  'curso.comecar': 'Começar: {topico}',
  'curso.concluido': 'Curso concluído',
  'curso.subseccoes.aria': 'Subsecções de {seccao}, por ordem',
  'curso.subseccao.aprender': 'Aprender',
  'curso.subseccao.praticar': 'Prática',
  'curso.subseccao.aria': '{titulo}, {tipo}: {feitas} de {total} tópicos concluídos. Abre a lista de tópicos.',
  'curso.topicos.aria': 'Tópicos de {subseccao}, por ordem',
  'curso.topicos.fechar': 'Fechar',
  'curso.topico.exercicios': '{n} exercícios',
  'curso.topico.emCurso': '{feitas}/{total} exercícios',
  'curso.topico.concluido': 'Concluído',
  'curso.topico.bloqueado': 'Depois de «{titulo}»',
  'curso.topico.bloqueadoEstado': 'Bloqueado.',
  'curso.topico.aria.disponivel': '{titulo}. Disponível, {exercicios} exercícios.',
  'curso.topico.aria.emCurso': '{titulo}. Em curso, {feitas} de {total} exercícios feitos. Continuar.',
  'curso.topico.aria.concluida': '{titulo}. Concluído, {exercicios} exercícios. Podes rever.',
  'curso.topico.aria.bloqueada': '{titulo}. Bloqueado. Disponível depois de concluíres «{falta}».',
  'curso.seguinte.etiqueta': 'Secção seguinte',
  'curso.seguinte.accao': 'Continuar a aprender',
  'curso.certificado.titulo': 'Certificado de conclusão',
  'curso.certificado.aria': 'Certificado de conclusão de {curso}: {percentagem}% concluído, {feitas} de {total} secções. Abrir os certificados.',
} as const;

export type ChaveTexto = keyof typeof ptPT;
